/**
 * TransitionProposalCard (TASK-617)
 *
 * One card per detected transition (job change): Ends/New rows with dates,
 * confidence badge, provenance line, "Affects:" chips mirroring the
 * server-side accept cascade. Never part of Apply-All — transitions are
 * always individually reviewed.
 */

import React, { useState } from 'react';
import { ArrowRightLeft, Check, Pencil, Quote, X } from 'lucide-react';
import { ConfidenceBadge } from '../ConfidenceBadge';
import type { StagedProposal, TransitionPayload } from '../../../services/kycProposalService';

interface TransitionProposalCardProps {
  proposal: StagedProposal;
  /** "Affects:" chips for this domain (served by the list endpoint). */
  affectsChips: string[];
  /** Current flat values of the domain, e.g. { employer_name: 'Schneider AG' }. */
  currentFields: Record<string, unknown>;
  onAccept: (proposalId: string, overrides?: { valid_from?: string }) => void;
  onDismiss: (proposalId: string) => void;
  /** TASK-618: open the transcript evidence panel for meeting-sourced proposals. */
  onShowEvidence?: (proposal: StagedProposal) => void;
  busy?: boolean;
}

export interface TransitionSummary {
  endsLine: string | null;
  newLine: string;
  validFrom: string;
}

/** Human summary of a transition: what ends, what starts, when. Pure — tested. */
export function describeTransition(
  payload: TransitionPayload,
  currentFields: Record<string, unknown>
): TransitionSummary {
  const part = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
  const line = (fields: Record<string, unknown>) =>
    [part(fields.employer_name), part(fields.job_title)].filter(Boolean).join(' · ');

  const endsLine = line(currentFields) || null;
  const newLine = line(payload.opens.fields) || '(new position)';
  return { endsLine, newLine, validFrom: payload.opens.valid_from };
}

const TransitionProposalCard: React.FC<TransitionProposalCardProps> = ({
  proposal,
  affectsChips,
  currentFields,
  onAccept,
  onDismiss,
  onShowEvidence,
  busy = false,
}) => {
  const payload = proposal.payload as TransitionPayload;
  const [editingDates, setEditingDates] = useState(false);
  const [validFrom, setValidFrom] = useState(payload.opens.valid_from);

  const summary = describeTransition(payload, currentFields);
  const quote = proposal.source_ref?.quote;

  return (
    <div
      data-testid="transition-proposal-card"
      className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3"
    >
      {/* Header */}
      <div className="flex items-center gap-2">
        <ArrowRightLeft className="w-4 h-4 text-amber-600" />
        <span className="text-sm font-semibold text-amber-900 capitalize">
          {proposal.domain} change
        </span>
        {proposal.confidence != null && (
          <ConfidenceBadge
            confidence={proposal.confidence}
            fieldName={`${proposal.domain} change`}
            size="sm"
          />
        )}
      </div>

      {/* Ends / New rows */}
      <div className="space-y-1.5 text-sm">
        {summary.endsLine && (
          <div className="flex items-baseline gap-2">
            <span className="w-10 flex-shrink-0 text-xs font-medium uppercase text-gray-500">
              Ends
            </span>
            <span className="text-gray-700">{summary.endsLine}</span>
            <span className="text-xs text-gray-500">
              until {payload.closes?.valid_to ?? validFrom}
            </span>
          </div>
        )}
        <div className="flex items-baseline gap-2">
          <span className="w-10 flex-shrink-0 text-xs font-medium uppercase text-emerald-600">
            New
          </span>
          <span className="font-medium text-gray-900">{summary.newLine}</span>
          <span className="text-xs text-gray-500">from {validFrom}</span>
        </div>
      </div>

      {/* Provenance */}
      {quote && (
        <div className="flex items-start gap-1.5 text-xs text-gray-600 italic">
          <Quote className="w-3 h-3 mt-0.5 flex-shrink-0" />
          <span>
            “{quote}”
            {proposal.source_kind === 'meeting' && ' — meeting transcript'}
          </span>
          {proposal.source_kind === 'meeting' && proposal.source_ref?.meeting_id && onShowEvidence && (
            <button
              onClick={() => onShowEvidence(proposal)}
              className="not-italic text-blue-600 hover:underline flex-shrink-0"
            >
              Show in transcript
            </button>
          )}
        </div>
      )}

      {/* Affects chips — exactly the server-side cascade */}
      {affectsChips.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs text-gray-500">Affects:</span>
          {affectsChips.map((chip) => (
            <span
              key={chip}
              className="text-xs text-amber-800 bg-amber-100 border border-amber-200 px-2 py-0.5 rounded-full"
            >
              {chip}
            </span>
          ))}
        </div>
      )}

      {/* Edit dates */}
      {editingDates && (
        <label className="flex items-center gap-2 text-xs text-gray-700">
          New position starts
          <input
            type="date"
            value={validFrom}
            onChange={(e) => setValidFrom(e.target.value)}
            className="border border-gray-300 rounded-md px-2 py-1 text-xs"
          />
        </label>
      )}

      {/* Actions */}
      <div className="flex items-center gap-1.5">
        <button
          disabled={busy}
          onClick={() =>
            onAccept(
              proposal.id,
              validFrom !== payload.opens.valid_from ? { valid_from: validFrom } : undefined
            )
          }
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-white bg-emerald-600 rounded-md hover:bg-emerald-700 disabled:opacity-50 transition-colors"
        >
          <Check className="w-3.5 h-3.5" />
          Accept transition
        </button>
        <button
          disabled={busy}
          onClick={() => setEditingDates((v) => !v)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <Pencil className="w-3.5 h-3.5" />
          Edit dates
        </button>
        <button
          disabled={busy}
          onClick={() => onDismiss(proposal.id)}
          className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-md hover:bg-gray-50 disabled:opacity-50 transition-colors"
        >
          <X className="w-3.5 h-3.5" />
          Dismiss
        </button>
      </div>
    </div>
  );
};

export default TransitionProposalCard;
